import React, {useCallback, useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useSplioService} from '../../../hooks/use-splio-service'
import {useIntl, FormattedMessage} from 'react-intl'
import useMultiSite from '../../../hooks/use-multi-site'
import {
    Box,
    Text,
    Button,
    Flex,
    Heading,
    Stack,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import DynamicImage from '@app/components/dynamic-image'
import FieldsetForm from '../../../components/shared/ui/FieldsetForm'
import colors from '../../../theme/foundations/colors'
import {css} from '@emotion/react'
import {useNewsletterSubscriptionModal} from '../../../custom-contexts'
import {useForm} from 'react-hook-form'
import NewsletterSubscriptionModalFields from '../../../components/forms/newsletter-subscription-modal-fields'
import {setLocalStorageJSONItem} from '../../../utils/utils'
import Link from '@salesforce/retail-react-app/app/components/link'
import {findDialCodeByCountry} from '@app/utils/utils'
import {HOME_HREF} from '../../../constants'

/**
 * Component for rendering a newsletter subscription modal with different states.
 * The modal displays different content based on the subscription status of the user.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.initialSubtitle - The subtitle displayed when the modal is in the initial state.
 * @param {Object} props.initialImage - The image displayed when the modal is in the initial state.
 * @param {string} props.initialImageAlt - The alt text for the initial image.
 * @param {string} props.legalMentionText - Legal mention text displayed in the initial state.
 * @param {string} props.alreadySubscribedSubtitle - The subtitle displayed when the user is already subscribed.
 * @param {Object} props.alreadySubscribedImage - The image displayed when the user is already subscribed.
 * @param {string} props.alreadySubscribedImageAlt - The alt text for the already subscribed image.
 * @param {string} props.subscriptionSuccessfulSubtitle - The subtitle displayed when the subscription is successful.
 * @param {Object} props.subscriptionSuccessfulImage - The image displayed when the subscription is successful.
 * @param {string} props.subscriptionSuccessfulImageAlt - The alt text for the subscription successful image.
 *
 * @returns {JSX.Element} The rendered newsletter subscription modal component.
 */
export const PDCNewsletterSubscription = ({
    initialSubtitle,
    initialImage,
    initialImageAlt,
    legalMentionText,
    alreadySubscribedSubtitle,
    alreadySubscribedImage,
    alreadySubscribedImageAlt,
    subscriptionSuccessfulSubtitle,
    subscriptionSuccessfulImage,
    subscriptionSuccessfulImageAlt,
    custom
}) => {
    const [modalState, setModalState] = useState('initial')

    const form = useForm()

    const {formatMessage} = useIntl()

    const isMobile = useBreakpointValue({
        base: true,
        md: false
    })

    const {onClose} = useNewsletterSubscriptionModal()

    const {site, locale} = useMultiSite()

    /**
     * Retrieves the active image object based on the current modal state.
     * Utilizes a callback to determine which image should be displayed by checking
     * the `isActive` property of each image object in the predefined array.
     *
     * @returns {Object} The active image object containing the image source and alt text.
     *                    Returns `undefined` if no active image is found.
     * @property {string} image - The URL of the image.
     * @property {string} alt - The alternative text for the image.
     * @property {boolean} isActive - A flag indicating if the image is active based on the modal state.
     */
    const getActiveImage = useCallback(() => {
        const imagesArray = [
            {
                image: custom?.initialImage || initialImage,
                alt: initialImageAlt,
                isActive:
                    (custom?.initialImage?.disBaseLink || initialImage.url) &&
                    modalState === 'initial'
            },
            {
                image: custom?.alreadySubscribedImage || alreadySubscribedImage,
                alt: alreadySubscribedImageAlt,
                isActive:
                    (custom?.alreadySubscribedImage?.disBaseLink || alreadySubscribedImage.url) &&
                    modalState === 'alreadySubscribed'
            },
            {
                image: custom?.subscriptionSuccessfulImage || subscriptionSuccessfulImage,
                alt: subscriptionSuccessfulImageAlt,
                isActive:
                    (custom?.subscriptionSuccessfulImage?.disBaseLink ||
                        subscriptionSuccessfulImage.url) &&
                    modalState === 'subscriptionSuccessful'
            }
        ]

        return imagesArray.find((image) => image.isActive)
    }, [modalState])

    const [activeImage, setActiveImage] = useState(getActiveImage())

    useEffect(() => {
        setActiveImage(getActiveImage())
    }, [modalState])

    const modalActiveTitle = {
        initial: formatMessage({
            id: 'newsletter_subscription_modal.title_join',
            defaultMessage: 'Join our family'
        }),
        alreadySubscribed: formatMessage({
            id: 'newsletter_subscription_modal.title_already_subscribed',
            defaultMessage: 'Splish, splosh...'
        }),
        subscriptionSuccessful: formatMessage({
            id: 'newsletter_subscription_modal.title_subscription_successful',
            defaultMessage: 'Endless summer awaits you...'
        })
    }

    const {isAlreadySubscribed, isBlackListedEmail, forwardAccountToSplio} = useSplioService()

    const modalActiveSubtitle = {
        initial: initialSubtitle || '',
        alreadySubscribed: alreadySubscribedSubtitle || '',
        subscriptionSuccessful: subscriptionSuccessfulSubtitle || ''
    }

    /**
     * Handles the submission of the newsletter subscription form. This function checks if the user is already subscribed
     * and updates the modal state accordingly. If the user is not subscribed, it forwards the subscription details to an
     * external service and updates the modal state to indicate a successful subscription.
     *
     * @function handleSubmit
     * @param {Object} data - The data object containing user input from the subscription form.
     * @param {string} data['newsletter-subscription-modal-email'] - The email address entered by the user.
     * @param {string} [data['newsletter-subscription-modal-firstName']] - The first name entered by the user (optional).
     * @param {string} [data['newsletter-subscription-modal-lastName']] - The last name entered by the user (optional).
     * @param {string} data['newsletter-subscription-modal-phone'] - The phone number entered by the user.
     * @returns {Promise<void>} A promise that resolves when the subscription process is complete.
     */
    const handleSubmit = useCallback(async (data) => {
        const siteCountry = site.alias

        const checkExistingOrBlacklistedRequestBody = {
            email: data['newsletter-subscription-modal-email'],
            siteId: site.id,
            locale: locale.id,
            requestType: 'pdc-newsletter-subscription'
        }

        const isEmailBlackListedEmail = await isBlackListedEmail(
            checkExistingOrBlacklistedRequestBody
        )

        if (isEmailBlackListedEmail.success) {
            setModalState('alreadySubscribed')
            setLocalStorageJSONItem('newsletterModalUsed', true)
            return false
        }

        const isUserAlreadySubscribed = await isAlreadySubscribed(
            checkExistingOrBlacklistedRequestBody
        )

        if (isUserAlreadySubscribed.success) {
            setModalState('alreadySubscribed')
        } else {
            const phoneCountry = data['newsletter-subscription-modal-phoneCountry']
            const phoneNumber = data['newsletter-subscription-modal-phone']
            const formattedPhone = phoneNumber
                ? `${findDialCodeByCountry(phoneCountry)} ${phoneNumber}`.trim()
                : ''

            const subscribeRequestBody = {
                email: data['newsletter-subscription-modal-email'],
                firstName: data['newsletter-subscription-modal-firstName'] || '',
                lastName: data['newsletter-subscription-modal-lastName'] || '',
                phone: formattedPhone,
                source: `popup-${siteCountry}`,
                landingpage: 'registrationpopup',
                marketingConsent: true,
                newsletterSection: 'body',
                requestType: 'pdc-newsletter-subscription'
            }

            await forwardAccountToSplio(subscribeRequestBody)
            setModalState('subscriptionSuccessful')
        }

        setLocalStorageJSONItem('newsletterModalUsed', true)
    }, [])

    /**
     * Handles the click event for the newsletter subscription modal.
     * Sets a flag in local storage to indicate that the newsletter modal has been used,
     * and then triggers the onClose function to close the modal.
     */
    const onClickHandler = useCallback(() => {
        setLocalStorageJSONItem('newsletterModalUsed', true)
        onClose()
    }, [])

    return (
        <Flex direction="row" alignItems="center">
            {!isMobile && (
                <Box flex={['1 0 50%']}>
                    {activeImage && (
                        <DynamicImage
                            src={`${
                                activeImage.image?.disBaseLink || activeImage.image?.url
                            }[?sw={width}]`}
                            widths={[395]}
                            imageProps={{
                                width: '100%',
                                loading: 'lazy',
                                alt: activeImage.alt || '',
                                htmlWidth: activeImage.image?.metaData?.width,
                                htmlHeight: activeImage.image?.metaData?.height
                            }}
                        />
                    )}
                </Box>
            )}
            <Box flex={['1 0 50%']} padding={['33px 24px 15px']}>
                {modalActiveTitle?.[modalState] && (
                    <Heading
                        as="h2"
                        fontSize="32px"
                        mb="20px"
                        textTransform="uppercase"
                        padding={['0 28px']}
                        textAlign={modalState === 'subscriptionSuccessful' ? 'left' : 'center'}
                    >
                        {modalActiveTitle[modalState]}
                    </Heading>
                )}
                {modalActiveSubtitle?.[modalState] && (
                    <Text
                        variant={'bodyBase2'}
                        dangerouslySetInnerHTML={{
                            __html: modalActiveSubtitle?.[modalState]
                        }}
                    />
                )}

                {modalState === 'initial' ? (
                    <Box marginTop="10px">
                        <FieldsetForm
                            id="newsletter-subscription-modal-from"
                            legendText={formatMessage({
                                defaultMessage: 'Subscribe to newsletter',
                                id: 'newsletter_subscription_modal.form_legend'
                            })}
                            onSubmit={form.handleSubmit(handleSubmit)}
                            error={form.formState.errors?.global?.message}
                        >
                            <Stack spacing={16}>
                                <NewsletterSubscriptionModalFields
                                    form={form}
                                    prefix={'newsletter-subscription-modal-'}
                                    isFirstInputFocus={true}
                                />
                                <Button
                                    type="submit"
                                    onClick={() => form.clearErrors('global')}
                                    isLoading={form.formState.isSubmitting}
                                    variant="primary"
                                >
                                    <FormattedMessage
                                        defaultMessage="Subscribe"
                                        id="newsletter_subscription_modal.button_subscribe"
                                    />
                                </Button>
                            </Stack>
                        </FieldsetForm>
                    </Box>
                ) : (
                    <Button
                        width="100%"
                        as={Link}
                        href={HOME_HREF}
                        variant="black"
                        margin={['20px 0']}
                        textTransform="uppercase"
                        color={colors.white}
                        bg={colors.fullBlack}
                        onClick={onClickHandler}
                        textDecoration="none"
                        _hover={{
                            textDecoration: 'none'
                        }}
                    >
                        {formatMessage({
                            id: 'newsletter_subscription_modal.button_success',
                            defaultMessage: 'Splash on!'
                        })}
                    </Button>
                )}

                {modalState === 'initial' && legalMentionText && (
                    <Text
                        variant={'bodyBase2'}
                        dangerouslySetInnerHTML={{
                            __html: legalMentionText
                        }}
                        fontSize="11px"
                        marginTop="18px"
                        css={css`
                            a {
                                text-decoration: underline;
                            }
                        `}
                    />
                )}

                {modalState === 'alreadySubscribed' && (
                    <Text variant={'bodyBase2'}>
                        {formatMessage({
                            id: 'newsletter_subscription_modal.disclaimer1',
                            defaultMessage: 'Offer valid for new emails only.'
                        })}
                    </Text>
                )}

                {modalState === 'subscriptionSuccessful' && (
                    <Text variant={'bodyBase2'}>
                        {formatMessage({
                            id: 'newsletter_subscription_modal.disclaimer2',
                            defaultMessage:
                                '*Discount code is not automatically applied to any order'
                        })}
                    </Text>
                )}
            </Box>
        </Flex>
    )
}

PDCNewsletterSubscription.propTypes = {
    initialSubtitle: PropTypes.string,
    initialImage: PropTypes.object,
    initialImageAlt: PropTypes.string,
    legalMentionText: PropTypes.string,
    alreadySubscribedSubtitle: PropTypes.string,
    alreadySubscribedImage: PropTypes.object,
    alreadySubscribedImageAlt: PropTypes.string,
    subscriptionSuccessfulSubtitle: PropTypes.string,
    subscriptionSuccessfulImage: PropTypes.object,
    subscriptionSuccessfulImageAlt: PropTypes.string,
    custom: PropTypes.object
}
